import React, { useEffect, useRef } from 'react';

interface BackgroundGridProps {
  opacity?: number;
  color?: string;
  showNumbers?: boolean;
  asymmetrical?: boolean;
  fadeAreas?: Array<'top' | 'bottom' | 'left' | 'right' | 'center'>;
}

// Generate a unique random seed for this component instance
const generateRandomSeed = () => Math.floor(Math.random() * 10000);
// Create a stable seed that's shared across instances
const STABLE_SEED = 8742; // An arbitrary fixed number

const BackgroundGrid: React.FC<BackgroundGridProps> = ({
  opacity = 0.08,
  color = 'var(--color-dark-brown)',
  showNumbers = true,
  asymmetrical = true,
  fadeAreas = ['center', 'top']
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Use the stable seed instead of a randomly generated one each time
  const randomSeedRef = useRef(STABLE_SEED);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track if the initial pattern has been drawn
  const hasInitializedRef = useRef(false);
  
  // Pseudo-random number generator with seed for consistent randomness
  const seededRandom = (min = 0, max = 1, seed = 0) => {
    const x = Math.sin(seed) * 10000;
    const result = (x - Math.floor(x));
    return min + result * (max - min);
  };
  
  // Helper to get opacity at a specific point based on fadeAreas
  const getOpacityAtPoint = (x: number, y: number, width: number, height: number) => {
    let opacity = 1.0;
    
    // Skip if no fade areas
    if (!fadeAreas || fadeAreas.length === 0) return opacity;
    
    // Calculate distance from center for center fade
    if (fadeAreas.includes('center')) {
      const centerX = width / 2;
      const centerY = height / 2;
      const maxDist = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
      const dist = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
      
      // Adjust opacity based on distance from center
      const centerFade = 0.3 + (0.7 * (dist / maxDist));
      opacity = Math.min(opacity, centerFade);
    }
    
    // Top fade
    if (fadeAreas.includes('top') && y < height * 0.3) {
      const topFade = y / (height * 0.3);
      opacity = Math.min(opacity, topFade);
    }
    
    // Bottom fade
    if (fadeAreas.includes('bottom') && y > height * 0.7) {
      const bottomFade = (height - y) / (height * 0.3);
      opacity = Math.min(opacity, bottomFade);
    }
    
    // Left fade
    if (fadeAreas.includes('left') && x < width * 0.3) {
      const leftFade = x / (width * 0.3);
      opacity = Math.min(opacity, leftFade);
    }
    
    // Right fade
    if (fadeAreas.includes('right') && x > width * 0.7) {
      const rightFade = (width - x) / (width * 0.3);
      opacity = Math.min(opacity, rightFade);
    }
    
    return opacity;
  };
  
  // Generate random lines based on position
  const generateRandomLines = (canvasWidth: number, canvasHeight: number, ctx: CanvasRenderingContext2D) => {
    const seed = randomSeedRef.current;
    const lineCount = Math.floor(seededRandom(15, 25, seed));
    
    // Create random lines
    for (let i = 0; i < lineCount; i++) {
      const seedOffset = seed + i * 100;
      
      // Generate random line properties
      const x1 = seededRandom(0, canvasWidth, seedOffset);
      const y1 = seededRandom(0, canvasHeight, seedOffset + 1);
      
      // Only allow horizontal or vertical lines (0, 90, 180, or 270 degrees)
      const isHorizontal = Math.floor(seededRandom(0, 2, seedOffset + 2)) === 0;
      let x2, y2;
      
      if (isHorizontal) {
        // Horizontal line
        const length = seededRandom(50, canvasWidth * 0.7, seedOffset + 3);
        // Randomly choose direction (left or right)
        const goRight = Math.floor(seededRandom(0, 2, seedOffset + 4)) === 0;
        x2 = goRight ? x1 + length : x1 - length;
        y2 = y1; // Same y-coordinate for horizontal line
      } else {
        // Vertical line
        const length = seededRandom(50, canvasHeight * 0.7, seedOffset + 3);
        // Randomly choose direction (up or down)
        const goDown = Math.floor(seededRandom(0, 2, seedOffset + 4)) === 0;
        x2 = x1; // Same x-coordinate for vertical line
        y2 = goDown ? y1 + length : y1 - length;
      }
      
      // Set line style - some dashed, some solid
      if (i % 3 === 0) {
        ctx.setLineDash([5, 10]);
      } else {
        ctx.setLineDash([]);
      }
      
      // Set opacity based on position
      const lineOpacity = getOpacityAtPoint(x1, y1, canvasWidth, canvasHeight);
      ctx.globalAlpha = opacity * lineOpacity;
      
      // Draw the line
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      
      // Add measurements to some lines (about 30%)
      if (showNumbers && i % 3 === 0) {
        const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        
        ctx.globalAlpha = opacity * 1.2;
        ctx.fillText(`${Math.round(distance / 10)} cm`, midX, midY - 5);
      }
    }
  };
  
  // Add pattern-making notation at random points
  const addPatternNotations = (canvasWidth: number, canvasHeight: number, ctx: CanvasRenderingContext2D) => {
    const seed = randomSeedRef.current;
    const notationCount = Math.floor(seededRandom(5, 10, seed + 500));
    
    // Set notation style
    ctx.setLineDash([]);
    ctx.globalAlpha = opacity * 1.5;
    
    for (let i = 0; i < notationCount; i++) {
      const seedOffset = seed + i * 200 + 500;
      
      const x = seededRandom(50, canvasWidth - 50, seedOffset);
      const y = seededRandom(50, canvasHeight - 50, seedOffset + 1);
      const notationType = Math.floor(seededRandom(0, 4, seedOffset + 2));
      
      const pointOpacity = getOpacityAtPoint(x, y, canvasWidth, canvasHeight);
      ctx.globalAlpha = opacity * pointOpacity * 1.5;
      
      // Draw different types of pattern notations
      switch (notationType) {
        case 0: // X mark
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillText('x', x, y + 15);
          break;
        case 1: // Measurement point
          {
            const direction = Math.floor(seededRandom(0, 4, seedOffset + 3));
            const length = seededRandom(30, 80, seedOffset + 4);
            
            let x2 = x, y2 = y;
            
            // Line extends in 4 possible directions
            switch (direction) {
              case 0: x2 = x + length; break; // Right
              case 1: x2 = x - length; break; // Left
              case 2: y2 = y + length; break; // Down
              case 3: y2 = y - length; break; // Up
            }
            
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            
            // Add arrow at one end
            ctx.beginPath();
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fill();
            
            // Label
            const measurement = Math.round(length / 10);
            ctx.fillText(`${measurement}`, (x + x2) / 2, (y + y2) / 2 - 5);
          }
          break;
        case 2: // Pattern shape corner
          {
            const size = seededRandom(15, 40, seedOffset + 3);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + size, y);
            ctx.lineTo(x + size, y + size);
            ctx.stroke();
          }
          break;
        case 3: // Technical note
          {
            const notes = ['dart', 'fold', 'cut', 'seam'];
            const noteIndex = Math.floor(seededRandom(0, notes.length, seedOffset + 3));
            ctx.fillText(notes[noteIndex], x, y);
          }
          break;
      }
    }
  };
  
  // Draw the actual grid with measurements
  const drawGrid = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear previous drawing
    ctx.clearRect(0, 0, width, height);
    
    // Set line style
    ctx.strokeStyle = color;
    ctx.lineWidth = 0.8; // Increased line width for better visibility
    ctx.globalAlpha = opacity;
    
    // Set text style for measurements
    ctx.font = '12px var(--font-technical, "Space Mono", monospace)'; // Increased font size
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    
    // Generate random lines instead of a grid
    generateRandomLines(width, height, ctx);
    
    // Add pattern-making notations
    if (showNumbers) {
      addPatternNotations(width, height, ctx);
    }
    
    // Mark as initialized
    hasInitializedRef.current = true;
    
  }, [opacity, color, showNumbers, asymmetrical, fadeAreas]);
  
  // Draw the grid on canvas - only on size changes, not on scrolling
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    // Set canvas size to match container
    const updateCanvasSize = () => {
      // Only update if the size actually changed
      if (canvas.width !== container.offsetWidth || 
          canvas.height !== container.offsetHeight || 
          !hasInitializedRef.current) {
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
        drawGrid();
      }
    };

    // Initial size setup
    updateCanvasSize();
    
    // Redraw on resize
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [drawGrid]);

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <div className="absolute inset-0 paper-texture-super-heavy pointer-events-none" aria-hidden="true"></div>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      />
    </div>
  );
};

export default BackgroundGrid; 